/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import Text from 'src/components/ts-components/Text'
import colors from '@sportninja/common/constants/appColors'
import { useRegistrationSetupContext } from '../../context'
import RichTextEditor from '../CustomForm/RichTextEditor'
import SendEmailIcon from 'src/components/icons/SendEmailIcon'
import { App, Alert, message as globalMessage } from 'antd'
import { useCallback, useState } from 'react'
import SendTestEmailModal from './SendTestEmailModal/index'

type TestEmailType = 'team' | 'player'

const EmailCustomization = () => {
  const { state, setState } = useRegistrationSetupContext()
  const { message } = App.useApp()
  const [errorBanner, setErrorBanner] = useState<string | null>(null)
  const [openType, setOpenType] = useState<TestEmailType | null>(null)

  const handleOpen = useCallback((type: TestEmailType) => {
    setErrorBanner(null)
    setOpenType(type)
  }, [])

  const handleClose = useCallback(() => {
    setOpenType(null)
  }, [])

  const handleSend = useCallback(
    async (email: string) => {
      try {
        // TODO: Integrate with API to send test emails
        // For now, just log and show success
        console.log(`Sending ${openType} test email to:`, email)
        // Ensure message renders at body level (outside modal stacking)
        globalMessage.config({ getContainer: () => document.body })
        message.open({
          type: 'success',
          content: 'Test email sent successfully',
          style: { zIndex: 11000 },
        })
        setOpenType(null)
      } catch (e) {
        setErrorBanner('Failed to send test email. Please try again.')
      }
    },
    [message, openType]
  )

  const showTeam = state.enableTeamRegistration
  const showPlayer = state.enablePlayerRegistration

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 16px;
      `}
    >
      {/* Header row: title + actions */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          align-self: stretch;
          width: 100%;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Email Customization
        </Text>

        {/* Action buttons (match TeamCustomForm preview style) */}
        <div
          css={css`
            display: inline-flex;
            align-items: center;
            gap: 16px;
          `}
        >
          {showTeam && (
            <div
              onClick={() => handleOpen('team')}
              css={css`
                border: 1px solid ${colors.PRIMARY};
                border-radius: 8px;
                padding: 4px 12px;
                justify-content: flex-start;
                align-items: center;
                gap: 8px;
                display: flex;
                cursor: pointer;
                text-decoration: none;
                height: 32px;
                &:hover {
                  opacity: 0.8;
                }
                /* Hide for now */
                display: none;
              `}
            >
              <SendEmailIcon size={24} color={colors.PRIMARY} />
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.PRIMARY}
              >
                Send Team Test Email
              </Text>
            </div>
          )}
          {showPlayer && (
            <div
              onClick={() => handleOpen('player')}
              css={css`
                border: 1px solid ${colors.PRIMARY};
                border-radius: 8px;
                padding: 4px 12px;
                justify-content: flex-start;
                align-items: center;
                gap: 8px;
                display: flex;
                cursor: pointer;
                text-decoration: none;
                height: 32px;
                &:hover {
                  opacity: 0.8;
                }
                /* Hide for now */
                display: none;
              `}
            >
              <SendEmailIcon size={24} color={colors.PRIMARY} />
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.PRIMARY}
              >
                Send Player Test Email
              </Text>
            </div>
          )}
        </div>
      </div>

      {errorBanner && (
        <Alert
          type='error'
          showIcon
          message={errorBanner}
          closable
          onClose={() => setErrorBanner(null)}
        />
      )}

      {showTeam && (
        <div
          css={css`
            display: flex;
            align-items: flex-start;
            align-self: stretch;
            width: 100%;
          `}
        >
          <RichTextEditor
            label='Team Registration Custom Message'
            value={state.teamConfirmationEmailContent || ''}
            onChange={(value) =>
              setState({ ...state, teamConfirmationEmailContent: value })
            }
            placeholder='Enter the custom content for team confirmation emails...'
            helperText='This message will be displayed on the initial confirmation emails sent to the team'
          />
        </div>
      )}

      {showPlayer && (
        <div
          css={css`
            display: flex;
            align-items: flex-start;
            align-self: stretch;
            width: 100%;
          `}
        >
          <RichTextEditor
            label='Player Registration Custom Message'
            value={state.playerConfirmationEmailContent || ''}
            onChange={(value) =>
              setState({ ...state, playerConfirmationEmailContent: value })
            }
            placeholder='Enter the custom content for player confirmation emails...'
            helperText='This message will be displayed on the initial confirmation emails sent to players'
          />
        </div>
      )}

      {!showTeam && !showPlayer && (
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          Enable Team or Player registration to customize confirmation emails.
        </Text>
      )}

      <SendTestEmailModal
        isOpen={!!openType}
        onClose={handleClose}
        onSend={handleSend}
        type={openType || 'team'}
      />
    </div>
  )
}

export default EmailCustomization
